import React, { useCallback, useEffect, useState } from 'react';
import { track } from '@src/services/analytics';
import styled, { css } from 'styled-components';
import { device } from '@src/common/breakpoints';
import { useAppDispatch, useAppSelector, useBreakPoints } from '@src/core/hooks';
import { cartStateSelector, removeCartCoupon, setCartCoupon } from '@src/features/cart';
import {
  BodyLarge,
  BodyRegular,
  BodyRegularMedium,
  BodySmall,
  BodySmallSummary,
} from '@src/fonts/typography/Typography';
import useTranslate, { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import ErrorMessage from '@src/components/reusable/error/ErrorMessage';
import DotFalling from '@src/components/reusable/spinner/DotFalling';
import { LayoutColors } from '@src/core/theme';
import SuccessMessage from '@src/components/reusable/success/SuccessMessage';
import { formatCents } from '@src/common';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import {
  CartCoupon,
  CartCouponConstraintTypeEnum,
  CartCouponTypeEnum,
  CartCouponUsageTypeEnum,
} from '@src/core/buyerApiClient';
import CouponButtonIcon from '@src/icons/coupon-button-icon.svg';

const CartCouponArea = () => {
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const { currentBreakpoint, md } = useBreakPoints();

  const { couponErrors, isLoadingCoupon, cart } = useAppSelector(cartStateSelector);

  const [couponCode, setCouponCode] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const getErrorMessage = () => {
    if (couponErrors) {
      const values = couponErrors.map((key) => i18n(key as DefaultMessagesKeys));
      return values.join(', ');
    }
    return '';
  };

  const removeCoupon = () => {
    track('cartCouponRemoved', { couponCode });

    dispatch(removeCartCoupon());
    setCouponCode('');
    inputRef.current?.focus();
  };

  const applyCoupon = () => {
    track('cartCouponAdded', {
      couponCode,
    });

    dispatch(setCartCoupon(couponCode));
    setShowSuccessMessage(true);
  };

  const getButtonText = (type: 'submit' | 'reset') => {
    const Icon = type === 'submit' ? MdAdd : MdDeleteOutline;
    const i18nKey = type === 'submit' ? 'cart_discount_coupon_submit_button' : 'cart_remove_discount_button';

    if (isLoadingCoupon) {
      return <DotFalling />;
    }

    return currentBreakpoint >= md ? i18n(i18nKey) : <Icon size={22} color={LayoutColors.whiteLight} />;
  };

  const productListWithCoupon = useCallback(
    (coupon: CartCoupon) => {
      const productIds = coupon.productIds ?? [];

      if (!productIds.length) {
        return [];
      }

      const productNames: Set<string> = new Set();

      cart?.items?.forEach((item) => {
        const includesProd = productIds.includes(item.product.id);
        const shouldAdd =
          (coupon.constraintType === CartCouponConstraintTypeEnum.Include && includesProd) ||
          (coupon.constraintType === CartCouponConstraintTypeEnum.Exclude && !includesProd);

        if (shouldAdd) {
          productNames.add(item.product.name);
        }
      });

      return Array.from(productNames);
    },
    [cart],
  );

  const coupon = cart?.coupon;
  const fromValue = cart?.costPreview.fullPartialAmountCents ?? 0;
  const toValue = cart?.costPreview.totalAmountCents ?? 0;
  const difference = fromValue - toValue;
  const discount = (100 * difference) / fromValue;
  const formattedDiscount = coupon?.formattedDiscount;

  const productList = coupon ? productListWithCoupon(coupon) : [];

  useEffect(() => {
    if (cart?.coupon?.code) {
      setCouponCode(cart.coupon.code);
    }
  }, [cart?.coupon]);

  return (
    <Container>
      <CouponButton onClick={() => setShowCouponInput(true)} disableHover={!!discount || showCouponInput}>
        <CouponButtonIcon />
        {coupon || showCouponInput ? (
          <>
            <InputCouponArea
              ref={inputRef}
              value={couponCode}
              readOnly={!!discount}
              onChange={(e) => setCouponCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applyCoupon();
                }
              }}
            />
            {discount ? (
              <RemoveCoupon disabled={isLoadingCoupon} onClick={removeCoupon}>
                {getButtonText('reset')}
              </RemoveCoupon>
            ) : (
              <SubmitCoupon disabled={isLoadingCoupon} onClick={applyCoupon}>
                {getButtonText('submit')}
              </SubmitCoupon>
            )}
          </>
        ) : (
          <CouponButtonText>{i18n('cart_discount_coupon_button_title')}</CouponButtonText>
        )}
      </CouponButton>

      {discount ? (
        <>
          {showSuccessMessage ? (
            <SuccessMessage style={{ marginBottom: 0 }} text={i18n('cart_discount_coupon_applied_succefully')} />
          ) : null}

          <DiscountValueContainer>
            {coupon?.usageType === CartCouponUsageTypeEnum.Product ? (
              <DiscountProductListTitle>
                <strong>
                  {i18n(
                    (productList.length ?? 0) > 1
                      ? 'cart_discount_coupon_product_list'
                      : 'cart_discount_coupon_product',
                  )}
                </strong>{' '}
                {productList.join(', ')}
              </DiscountProductListTitle>
            ) : (
              <DiscountHeader>{i18n('cart_discount')}</DiscountHeader>
            )}
            <DiscountValue>
              {coupon?.type === CartCouponTypeEnum.Percentage ? <span>{`(-${formattedDiscount})`}</span> : null}
              {formatCents(difference)}
            </DiscountValue>
          </DiscountValueContainer>
        </>
      ) : null}

      {getErrorMessage() ? <ErrorMessage style={{ marginBottom: 0 }} text={getErrorMessage()} /> : null}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CouponButton = styled.div<{ disableHover: boolean }>`
  display: flex;
  height: 48px;
  padding: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 9px;
  align-self: stretch;
  border-radius: var(--radius-sm, 4px);
  border: 2px solid var(--Black-400, #cdcdcd);
  background: var(--Background-Light, #fff);

  /* Drop-shadow light */
  box-shadow: 0px 0px 0px 0px rgba(0, 0, 0, 0.1), 0px 0px 0px 0px rgba(0, 0, 0, 0.1),
    0px 1px 1px 0px rgba(0, 0, 0, 0.09), 0px 1px 1px 0px rgba(0, 0, 0, 0.05), 0px 2px 1px 0px rgba(0, 0, 0, 0.01),
    0px 4px 1px 0px rgba(0, 0, 0, 0);

  &:hover {
    ${({ disableHover }) =>
      disableHover
        ? ''
        : css`
            cursor: pointer;
            background: ${LayoutColors.successMain};
            box-shadow: none;
            border-color: ${LayoutColors.successMain};

            span {
              color: ${LayoutColors.whiteLight};
            }

            svg {
              path {
                stroke: ${LayoutColors.whiteLight} !important;
              }
            }
          `}
  }
`;

const CouponButtonText = styled.span`
  ${BodyLarge}

  ${device.smallTablet} {
    ${BodyRegularMedium}
  }
`;

const InputCouponArea = styled.input`
  ${BodyLarge}
  background: #ffffff;
  padding: 8px 16px;
  border: none;
  width: 100px;
  flex-grow: 1;
  outline: none;
  padding-bottom: 4px;
`;

interface SubmitCouponProps {
  disabled: boolean;
}
const SubmitCoupon = styled.button<SubmitCouponProps>`
  ${BodyRegularMedium}
  display: flex;
  justify-content: center;
  padding: 6px;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  color: ${LayoutColors.whiteLight};
  border-radius: 4px;
  border: 1px solid ${LayoutColors.successDark};
  background: ${LayoutColors.successMain};

  ${device.smallTablet} {
    padding: 0.25rem 1rem;
  }
`;

const RemoveCoupon = styled(SubmitCoupon)`
  padding: 6px;
  background: ${LayoutColors.greenDark};
  border-color: ${LayoutColors.greenDark} !important;

  ${device.smallTablet} {
    background: transparent;
    color: ${LayoutColors.greenDark};
  }
`;

const DiscountValueContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  flex-direction: row;
`;

const DiscountHeader = styled.p`
  ${BodySmall}

  ${device.mobile} {
    ${BodyRegularMedium}
  }
`;
const DiscountValue = styled.p`
  ${BodySmall}
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;

  ${device.mobile} {
    ${BodyRegular}
  }
`;

const DiscountProductListTitle = styled.p`
  ${BodySmallSummary}

  strong {
    font-weight: 700;
  }
`;

export default CartCouponArea;
